import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";

interface DropdownMenuContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.MutableRefObject<HTMLButtonElement | null>;
  contentRef: React.MutableRefObject<HTMLDivElement | null>;
}

const DropdownMenuContext = createContext<DropdownMenuContextValue | null>(
  null
);

function useDropdownMenu() {
  const ctx = useContext(DropdownMenuContext);
  if (!ctx) {
    throw new Error("DropdownMenu primitives must be used inside DropdownMenu");
  }
  return ctx;
}

interface DropdownMenuProps {
  children: ReactNode;
  className?: string;
}

/**
 * Lightweight popover-menu primitive — no Radix. Closes on outside
 * click + Escape. Caller composes ``DropdownMenuTrigger`` +
 * ``DropdownMenuContent`` + ``DropdownMenuItem``.
 *
 * The content panel is rendered through a portal to ``document.body``
 * and positioned ``fixed`` against the trigger. This keeps it from
 * being clipped by an ancestor's ``overflow-hidden`` — e.g. the
 * rounded ``overflow-hidden`` wrapper around data tables, where an
 * absolutely-positioned menu would otherwise be cut off.
 */
export function DropdownMenu({ children, className }: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      // The content is portaled out of this subtree, so an outside
      // click must be checked against both the trigger and the
      // portaled panel.
      if (
        !triggerRef.current?.contains(target) &&
        !contentRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    window.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  return (
    <DropdownMenuContext.Provider
      value={{ open, setOpen, triggerRef, contentRef }}
    >
      <div className={cn("relative", className)}>{children}</div>
    </DropdownMenuContext.Provider>
  );
}

interface DropdownMenuTriggerProps {
  children: ReactNode;
  className?: string;
  asChild?: boolean;
  "aria-label"?: string;
}

export function DropdownMenuTrigger({
  children,
  className,
  "aria-label": ariaLabel,
}: DropdownMenuTriggerProps) {
  const { open, setOpen, triggerRef } = useDropdownMenu();
  return (
    <button
      ref={triggerRef}
      type="button"
      onClick={() => setOpen(!open)}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-label={ariaLabel}
      className={cn(
        "rounded-md p-1.5 text-neutral-500 transition-colors hover:bg-ocean-100 hover:text-ocean-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ocean-400 focus-visible:ring-offset-2",
        open && "bg-ocean-100 text-ocean-600",
        className
      )}
    >
      {children}
    </button>
  );
}

interface DropdownMenuContentProps {
  children: ReactNode;
  className?: string;
  align?: "left" | "right";
}

interface Coords {
  top: number;
  left?: number;
  right?: number;
}

/** Vertical gap between the trigger and the menu panel (px). */
const MENU_GAP = 4;
/** Minimum margin the menu keeps from the viewport edge (px). */
const VIEWPORT_MARGIN = 8;

export function DropdownMenuContent({
  children,
  className,
  align = "right",
}: DropdownMenuContentProps) {
  const { open, triggerRef, contentRef } = useDropdownMenu();
  const [coords, setCoords] = useState<Coords | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    const update = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const r = trigger.getBoundingClientRect();
      const menuHeight = contentRef.current?.offsetHeight ?? 0;
      const below = r.bottom + MENU_GAP;
      // Flip above the trigger when the panel would overflow the
      // bottom of the viewport (e.g. a row-action menu near the
      // last rows of a long table).
      const overflowsBottom =
        menuHeight > 0 &&
        below + menuHeight > window.innerHeight - VIEWPORT_MARGIN;
      const top = overflowsBottom
        ? Math.max(VIEWPORT_MARGIN, r.top - MENU_GAP - menuHeight)
        : below;
      setCoords(
        align === "right"
          ? { top, right: Math.max(VIEWPORT_MARGIN, window.innerWidth - r.right) }
          : { top, left: Math.max(VIEWPORT_MARGIN, r.left) }
      );
    };
    update();
    // ``true`` capture phase so a scroll inside any ancestor
    // container — not just the window — keeps the panel anchored.
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, align, triggerRef, contentRef]);

  if (!open) return null;

  return createPortal(
    <div
      ref={contentRef}
      role="menu"
      style={{
        position: "fixed",
        top: coords?.top ?? -9999,
        left: coords?.left,
        right: coords?.right,
        // Hidden until measured so the panel never flashes at a
        // stale position on open.
        visibility: coords ? "visible" : "hidden",
      }}
      className={cn(
        "z-50 min-w-[160px] overflow-hidden rounded-md border border-ocean-100 bg-white shadow-md",
        className
      )}
    >
      {children}
    </div>,
    document.body
  );
}

interface DropdownMenuItemProps {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  variant?: "default" | "danger";
}

export function DropdownMenuItem({
  children,
  onClick,
  className,
  disabled = false,
  variant = "default",
}: DropdownMenuItemProps) {
  const { setOpen } = useDropdownMenu();
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={() => {
        if (disabled) return;
        onClick?.();
        setOpen(false);
      }}
      className={cn(
        "flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors",
        disabled && "cursor-not-allowed text-neutral-400",
        !disabled &&
          variant === "default" &&
          "text-ocean-800 hover:bg-ocean-50",
        !disabled &&
          variant === "danger" &&
          "text-danger-600 hover:bg-danger-50",
        className
      )}
    >
      {children}
    </button>
  );
}

export function DropdownMenuSeparator() {
  return <div className="my-1 h-px bg-ocean-100" />;
}
